// ReportList.js - Main Component
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ReportList.css';
import './mobileReportList.css';
import { createLogger } from '../services/Debug';
// Components
import ReportListSidebar from './ReportListSidebar';
import ReportListMainContent from './ReportListMainContent';
import ReportListLoader from './ReportListLoader';
import RecorderSidePanel from '../components/RecorderSidePanel/RecorderSidePanel';
import { useRecorderPanel } from '../context/RecorderPanelContext';
import Notification from '../components/Notifications/Notification';
import Toggle from '../components/Toggle/Toggle';
import Button from '../components/buttons/Button';
import Card from '../components/Card/Card';
import { FaTimes, FaDownload, FaBuilding, FaCopy, FaExclamationTriangle, FaFilePdf } from 'react-icons/fa';
import FullScreenTextEditor from '../components/editors/FullScreenTextEditor';

// Hooks
import { useReportManagement } from './useReportManagement';
import useNotification from '../components/Notifications/useNotifications';
import { useFullScreenTextEditor } from '../hooks/useFullScreenTextEditor';

import { parseReportSections, parseMailSections, downloadSectionsCSVFromText } from './parseReportSections';
import { renderBullseyeImageUnified, canRenderBullseyeImage } from './bullseyeImageRenderer';
import {
  capitalizeFirstLetter,
  createMailtoLink,
  filterReportsByDate,
  formatDate,
  saveReportsToLocalStorage,
  isReportStatusCheckable,
  canReportBeOpened,
  getReportContent,
  normalizeReport
} from './reportListUtils';

import { reportService, wordService, enterpriseAdminService, enterpriseService, adminService, reportTypeService } from '../services';
import { useAuth } from '../context/AuthContext';

// Platform detection
import { isElectron, isCapacitor } from '../utils/platform';
import { Clipboard } from '@capacitor/clipboard';


// Constants
import { DATE_FILTERS, SMALL_SCREEN_BREAKPOINT } from './reportListConstants';
import { ReportTypeNames } from '../models/ReportTypeNames';

export default function ReportList() {
  // ===== HOOKS =====
  const {
    reports,
    setReports,
    loading,
    loadingText,
    extractedReportTypes,
    isRefreshingRef,
    handleRefresh,
    archiveReport: archiveReportAPI,
    improveReportWithAI,
    changeReportType
  } = useReportManagement();

  const { notification, showNotification, hideNotification } = useNotification();
  const { profile } = useAuth();
  const { isOpen: isRecorderPanelOpen, isWebOnly } = useRecorderPanel();
  const log = createLogger('ReportList');
  // ===== STATE =====
  // Report state
  const [selectedReport, setSelectedReport] = useState(null);
  const [mdReport, setMdReport] = useState('');
  
  // UI state
  const [isInsertMode, setIsInsertMode] = useState(false);
  const [sections, setSections] = useState([]);
  const [copiedSections, setCopiedSections] = useState([]); // Track which sections have been copied
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < SMALL_SCREEN_BREAKPOINT);
  const [currentVisibleGroup] = useState(formatDate(new Date()));
  const [caretPos, setCaretPos] = useState(0);
  
  // [?] detection state
  const [hasUncertaintyMarkers, setHasUncertaintyMarkers] = useState(false);
  const [hasCrochetUncertaintyMarkers, setHasCrochetUncertaintyMarkers] = useState(false);
  const [hasWordUncertaintyMarkers, setHasWordUncertaintyMarkers] = useState(false);
  
  // Filter state
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [reportTypeFilter, setReportTypeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.ALL);
  const [showArchived, setShowArchived] = useState(false);
  // Enterprise filters now handled via advanced search
  
  // Centralized date filter change handler (persists to localStorage)
  const handleDateFilterChange = useCallback((newFilter) => {
    setDateFilter(newFilter);
    try {
      localStorage.setItem('dateFilter', newFilter);
    } catch (e) {
      // ignore
    }
  }, []);

  // Search handler that also sets date filter to 'all' when searching
  const handleSearchWithDateReset = useCallback((searchValue) => {
    setSearchQuery(searchValue);
    // When user starts searching, automatically switch to 'all' to show all results
    if (searchValue && dateFilter !== DATE_FILTERS.ALL) {
      handleDateFilterChange(DATE_FILTERS.ALL);
    }
  }, [dateFilter, handleDateFilterChange]);
  
  // Modal/Menu state
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showTextOptions, setShowTextOptions] = useState(false);
  const [showReportTypeOverlay, setShowReportTypeOverlay] = useState(false);
  const [showSchema, setShowSchema] = useState(false);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, reportId: null });
  const [showAnnexPanel, setShowAnnexPanel] = useState(false);
  const [showDestinatairePanel, setShowDestinatairePanel] = useState(false);
  const [editedDestinataire, setEditedDestinataire] = useState('');
  const [defaultExportDir, setDefaultExportDir] = useState('');
  const [sortMonthDay, setSortMonthDay] = useState(false);
  // Load default export directory on mount (Electron)
  useEffect(() => {
    if (isElectron && window.electronAPI?.getDefaultExportDir) {
      window.electronAPI.getDefaultExportDir().then((dir) => {
        if (typeof dir === 'string' && dir.length > 0) {
          setDefaultExportDir(dir);
        }
      }).catch(() => {});
    }
    try {
      const saved = localStorage.getItem('export.sortMonthDay');
      if (saved === '1') setSortMonthDay(true);
      // Back-compat: legacy unified flag
      const legacy = localStorage.getItem('export.sortByDate');
      if (legacy === '1' && saved == null) setSortMonthDay(true);
    } catch (_) {}
  }, []);

  
  // Advanced search state
  const [isAdvancedSearchActive, setIsAdvancedSearchActive] = useState(false);
  const [advancedSearchResults, setAdvancedSearchResults] = useState([]);
  

  
  // Enterprise example creation state
  const [showEnterpriseSelectionModal, setShowEnterpriseSelectionModal] = useState(false);
  const [availableEnterprises, setAvailableEnterprises] = useState([]);
  const [enterpriseExampleLoading, setEnterpriseExampleLoading] = useState(false);
  const [adminExampleLoading, setAdminExampleLoading] = useState(false);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [availableEnterpriseReportTypes, setAvailableEnterpriseReportTypes] = useState([]);
  const [enterpriseReportTypesLoading, setEnterpriseReportTypesLoading] = useState(false);
  
  
  const isEnterpriseAdmin = profile?.isEnterpriseAdmin || false;
  
  const isSilverOrGoldOrEnterprise = profile?.isSilverOrGoldOrEnterprise;

  // Gold/Enterprise gating
  const silverGatingMsg = 'Réservé aux abonnements Silver et plus'
  const goldGatingMsg = 'Réservé aux abonnements Gold et Entreprise';


  // Report types for "Changer le modèle" overlay
  const [favoriteReportTypes, setFavoriteReportTypes] = useState([]);

  useEffect(() => {
    // Load default export directory if Electron
    if (isElectron && window.electronAPI?.getDefaultExportDir) {
      window.electronAPI.getDefaultExportDir().then((dir) => {
        if (typeof dir === 'string') setDefaultExportDir(dir);
      }).catch(() => {});
    }
    if (!showReportTypeOverlay) return;
    let isMounted = true;
    (async () => {
      try {
        const reportTypesData = await reportTypeService.getFavorites();
        let names = [];
        if (reportTypesData && typeof reportTypesData.getFavorites === 'function') {
          names = reportTypesData.getFavorites();
        } else if (Array.isArray(reportTypesData)) {
          names = reportTypesData;
        }
        if (isMounted) setFavoriteReportTypes(names || []);
      } catch (e) {
        try {
          const fallback = profile?.reportTypeFavorites?.getFavorites();
          if (isMounted) setFavoriteReportTypes(fallback || []);
        } catch (_) {
          if (isMounted) setFavoriteReportTypes([]);
        }
      }
    })();
    return () => { isMounted = false; };
  }, [showReportTypeOverlay]);

  
  // Helper function to format report type names for display
  const formatReportTypeName = (typeName) => ReportTypeNames.formatDisplayName(typeName);
  
  // Status checking state
  const [statusCheckingReports, setStatusCheckingReports] = useState(new Set());
  const [statusCheckResults, setStatusCheckResults] = useState(new Map());
  
  // Refresh cooldown state
  const [refreshCooldown, setRefreshCooldown] = useState(false);

  // ===== REFS =====
  const textareaRef = useRef(null);
  const isSendingRef = useRef(false);
  const autoSaveTimeoutRef = useRef(null);
  const hasHydratedDateFilterRef = useRef(false);

  // Setup fullscreen editor for Capacitor
  const {
    openEditor: openFullScreenEditor,
    editorProps: fullScreenEditorProps,
    isEnabled: isFullScreenEnabled
  } = useFullScreenTextEditor({
    title: selectedReport ? `Modifier ${formatReportTypeName(selectedReport.reportType)}` : 'Modifier le rapport',
    onSave: async (newText) => {
      setMdReport(newText);
      // Trigger save and auto-save
      if (selectedReport) {
        const updatedReport = normalizeReport(selectedReport);
        if (updatedReport.isCorrected) {
          updatedReport.correctedContent = newText;
        } else {
          updatedReport.content = newText;
        }
        
        setReports((prev) => {
          const updatedArr = prev.map((r) =>
            r.id === updatedReport.id ? updatedReport : r
          );
          saveReportsToLocalStorage(updatedArr);
          return updatedArr;
        });
        
        setSelectedReport(updatedReport);
      }
    },
    showCharCount: true,
    placeholder: 'Commencez à rédiger votre rapport médical...'
  });

  // Generator list removed; handled in advanced search

  // ===== HANDLER FUNCTIONS =====
  
  // [?] detection function
  const detectUncertaintyMarkers = useCallback((text) => {
    const hasCrochetMarkers = text && (text.includes('[?]') || text.includes('[?') || text.includes('?]'));
    const hasWordMarkers = text && (text.includes('non mentionné') || text.includes('non renseigné') || text.includes('non précisé'));
    setHasCrochetUncertaintyMarkers(hasCrochetMarkers);
    setHasWordUncertaintyMarkers(hasWordMarkers);
    const hasMarkers = hasCrochetMarkers || hasWordMarkers;
    setHasUncertaintyMarkers(hasMarkers);
    return hasMarkers;
  }, []);

  // Remove uncertainty markers from text
  const cleanUncertaintyMarkers = useCallback((text) => {
    if (!text) return '';
    let cleaned = text;
    // Remove bracket markers
    cleaned = cleaned.replace(/\[\?\]/g, '');
    cleaned = cleaned.replace(/\[\?/g, '');
    cleaned = cleaned.replace(/\?\]/g, '');
    // Remove word-based markers (case-insensitive)
    const phrases = ['non mentionné', 'non renseigné', 'non précisé'];
    phrases.forEach((p) => {
      const re = new RegExp(p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      cleaned = cleaned.replace(re, '');
    });
    // Normalize spaces
    cleaned = cleaned.replace(/[ \t]+/g, ' ');
    cleaned = cleaned.replace(/ +(\r?\n)/g, '$1');
    return cleaned.trim();
  }, []);

  // Handler to clean markers and persist
  const handleCleanUncertaintyMarkers = useCallback(() => {
    if (!selectedReport) return;
    const cleaned = cleanUncertaintyMarkers(mdReport);
    setMdReport(cleaned);
    // Update report object and persist
    const updatedReport = normalizeReport(selectedReport);
    if (updatedReport.isCorrected) {
      updatedReport.correctedContent = cleaned;
    } else {
      updatedReport.content = cleaned;
    }
    setReports((prev) => {
      const updatedArr = prev.map((r) => (r.id === updatedReport.id ? updatedReport : r));
      saveReportsToLocalStorage(updatedArr);
      return updatedArr;
    });
    setSelectedReport(updatedReport);
    // Recompute markers
    detectUncertaintyMarkers(cleaned);
    showNotification('Marqueurs d\'incertitude supprimés', 'success');
  }, [selectedReport, mdReport, cleanUncertaintyMarkers, setReports, detectUncertaintyMarkers, showNotification]);

  // Function to get preview text around uncertainty markers
  const getUncertaintyPreview = useCallback((text) => {
    if (!text) return '';
    
    const previews = [];
    // Process text to find all uncertainty markers without duplicates
    let index = 0;
    
    while (index < text.length) {
      // Look for the longest pattern first: [?]
      let foundIndex = text.indexOf('[?]', index);
      let markerLength = 3;
      let marker = '[?]';
      
      // If not found, look for [? or ?]
      if (foundIndex === -1) {
        foundIndex = text.indexOf('[?', index);
        if (foundIndex !== -1) {
          markerLength = 2;
          marker = '[?';
        } else {
          foundIndex = text.indexOf('?]', index);
          if (foundIndex !== -1) {
            markerLength = 2;
            marker = '?]';
          }
        }
      }
      
      if (foundIndex === -1) break;
      
      // Get 20 characters before the marker
      let start = Math.max(0, foundIndex - 20);
      
      // Find the nearest word boundary (space, newline, or start of text)
      while (start > 0 && !/\s/.test(text[start])) {
        start++;
      }
      
      const preview = text.substring(start, foundIndex + markerLength);
      previews.push(preview);
      
      // Move to next position
      index = foundIndex + markerLength;
    }
    
    // Also include previews for word-based uncertainty markers
    const wordMarkers = ['non mentionné', 'non renseigné', 'non précisé'];
    wordMarkers.forEach((phrase) => {
      let pos = 0;
      while (pos < text.length) {
        const foundIndex = text.indexOf(phrase, pos);
        if (foundIndex === -1) break;
        
        let start = Math.max(0, foundIndex - 20);
        while (start > 0 && !/\s/.test(text[start])) {
          start++;
        }
        
        const preview = text.substring(start, foundIndex + phrase.length);
        previews.push(preview);
        
        pos = foundIndex + phrase.length;
      }
    });
    
    // Remove duplicates and limit to first 3 previews
    const uniquePreviews = [...new Set(previews)].slice(0, 3);
    return uniquePreviews.join(' ... ');
  }, []);

  // Unified uncertainty confirmation function
  const confirmUncertaintyAction = useCallback((actionName) => {
    if (hasUncertaintyMarkers || hasWordUncertaintyMarkers) {
      const confirmed = window.confirm(`Attention : Ce rapport contient des marqueurs d'incertitude (${hasWordUncertaintyMarkers ? 'non mentionné' : ''} ${hasCrochetUncertaintyMarkers && hasWordUncertaintyMarkers ? 'et' : ''} ${hasCrochetUncertaintyMarkers ? '[?]' : ''}). Êtes-vous sûr de vouloir ${actionName} ?`);
      return confirmed;
    }
    return true; // If no uncertainty markers, always allow the action
  }, [hasUncertaintyMarkers, hasCrochetUncertaintyMarkers, hasWordUncertaintyMarkers]);
  
  // Insert mode management
  const stopInsertMode = useCallback(() => {
    if (window.electronAPI) {
      if (window.electronAPI.stopSectionInsertion) {
        window.electronAPI.stopSectionInsertion();
      }
      window.electronAPI.stopInsertMode();
    }
    setIsInsertMode(false);
    setCopiedSections([]); // Reset copied sections when exiting insert mode
  }, []);

  // Save handler
  const handleSave = useCallback((reportArg) => {
    const repToSave = reportArg || selectedReport;
    if (!repToSave) return;
    
    // Ensure we have a Report instance
    const updatedReport = normalizeReport(repToSave);
    
    // Update the content
    if (updatedReport.isCorrected) {
      updatedReport.correctedContent = mdReport;
    } else {
      updatedReport.content = mdReport;
    }
    
    setReports((prev) => {
      const updatedArr = prev.map((r) =>
        r.id === updatedReport.id ? updatedReport : r
      );
      saveReportsToLocalStorage(updatedArr);
      return updatedArr;
    });
    
    setSelectedReport(updatedReport);
  }, [mdReport, selectedReport, setReports]);

  // Debounced auto-save handler
  const debouncedAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      if (selectedReport) {
        handleSave();
      }
    }, 500);
  }, [selectedReport, handleSave]);

  // Refresh handler with cooldown
  const handleRefreshWithCooldown = useCallback(async () => {
    if (refreshCooldown) return;
    
    // Set cooldown
    setRefreshCooldown(true);
    
    // Call the original refresh handler
    await handleRefresh();
    
    // Start 3-second cooldown
    setTimeout(() => {
      setRefreshCooldown(false);
    }, 3000);
  }, [refreshCooldown, handleRefresh]);

  // Advanced search handlers
  const handleSubmitAdvancedSearch = useCallback(async ({ q = '', qTerms = [], qOperator = '', startDate = '', endDate = '', reportType = '', includeEnterprise = false, generatedBy = '', limit } = {}) => {
    if (!isSilverOrGoldOrEnterprise) {
      showNotification(silverGatingMsg, 'warning');
      return;
    }
    try {
      showNotification('Recherche avancée en cours...', 'info');
      const results = await reportService.searchReports({ q, qTerms, qOperator, startDate, endDate, reportType, includeEnterprise, generatedBy, limit: (typeof limit === 'number' ? limit : 50), offset: 0 });
      setAdvancedSearchResults(results);
      setIsAdvancedSearchActive(true);
      try {
        sessionStorage.setItem('advancedSearchResults', JSON.stringify(results));
      } catch (e) {
        // ignore storage errors
      }
      // Deselect current report if not part of results
     
      setSelectedReport(null);
      setMdReport('');
      handleDateFilterChange('all');
      showNotification('Recherche avancée terminée', 'success');
    } catch (error) {
      log.error('Erreur recherche avancée:', error);
      showNotification('Erreur lors de la recherche avancée', 'error');
    }
  }, [selectedReport, showNotification, isSilverOrGoldOrEnterprise]);

  const handleStopAdvancedSearch = useCallback(() => {
    setIsAdvancedSearchActive(false);
    setAdvancedSearchResults([]);
    try {
      sessionStorage.removeItem('advancedSearchResults');
    } catch (e) {
      // ignore
    }
  }, []);

  // Show live dictations using advanced search results pathway (no persistence)
  const handleShowLiveDictations = useCallback(async () => {
    try {
      showNotification('Chargement des dictées live...', 'info');
      const results = await reportService.getLiveReports();
      setAdvancedSearchResults(results || []);
      setIsAdvancedSearchActive(true);
      try {
        sessionStorage.setItem('advancedSearchResults', JSON.stringify(results || []));
      } catch (e) {
        // ignore storage errors
      }
      setSelectedReport(null);
      setMdReport('');
      handleDateFilterChange('all');
      showNotification('Dictées live affichées', 'success');
    } catch (error) {
      log.error('Erreur chargement dictées live:', error);
      showNotification('Erreur lors du chargement des dictées live', 'error');
    }
  }, [showNotification, handleDateFilterChange]);

  // Copy utilities
  const copyTextToClipboard = async (text) => {
    if (isCapacitor) {
      await Clipboard.write({ string: text });
      return Promise.resolve();
    }
    return navigator.clipboard.writeText(text);
  };

  const handleCopySection = useCallback((sectionText, key, number) => {
  

    return copyTextToClipboard(sectionText).then(() => {
      // Mark this section as copied
      if (number !== undefined && !copiedSections.includes(number)) {
        setCopiedSections(prev => [...prev, number]);
      }

      let message = 'Section copiée.';
      if (key) message = `${key} copié(e)`;
      if (number !== undefined) message += ` (raccourci : Ctrl + ${number + 1})`;
      showNotification(message, 'primary');
    }).catch(err => log.error('Erreur copie section :', err));
  }, [showNotification, confirmUncertaintyAction, copiedSections]);

  // Keyboard handler
  const handleKeyDown = useCallback((e) => {
    if (!selectedReport) return;

    if (isInsertMode && e.ctrlKey && !e.shiftKey) {
      const numberKey = parseInt(e.key, 10);
      if (numberKey >= 1 && numberKey <= sections.length) {
        e.preventDefault();
        const idx = numberKey - 1;
        const sec = sections[idx];
        if (sec) {
          // Check for uncertainty markers and show confirmation for keyboard shortcuts too
          if (!confirmUncertaintyAction('copier cette section')) return;
          
          handleCopySection(
            sec.content || '', 
            (sec.title && typeof sec.title === 'string' ? sec.title : null) || `Section ${idx + 1}`, 
            idx
          );
        }
      }
    }
  }, [isInsertMode, sections, selectedReport, handleCopySection, confirmUncertaintyAction]);

  // Editor handlers
  const handleEditorChange = useCallback((updatedText) => {
    if (isInsertMode) {
      stopInsertMode();
    }
    setShowExportMenu(false);
    setShowTextOptions(false);
    setMdReport(updatedText);
    
    // Detect [?] markers in the text
    detectUncertaintyMarkers(updatedText);
    
    // Trigger auto-save after content change
    debouncedAutoSave();
  }, [isInsertMode, stopInsertMode, debouncedAutoSave, detectUncertaintyMarkers]);

  const updateCaretPosition = useCallback(() => {
    if (!textareaRef.current) return;
    setCaretPos(textareaRef.current.selectionStart);
  }, []);

  // Report actions
  const handleArchiveReport = useCallback(async (reportId) => {
    if (isInsertMode) {
      stopInsertMode();
    }
    
    try {
      await archiveReportAPI(reportId);
      
      setReports((prevReports) => {
        const updatedReports = prevReports.map((r) => {
          if (r.id === reportId) {
            const updatedReport = normalizeReport(r);
            updatedReport.isArchived = true;
            return updatedReport;
          }
          return r;
        });
        
        saveReportsToLocalStorage(updatedReports);
        return updatedReports;
      });
      
      if (selectedReport && selectedReport.id === reportId) {
        setSelectedReport(null);
      }
      
      showNotification('Rapport archivé avec succès', 'info');
    } catch (error) {
      log.error('Error archiving report:', error);
      showNotification('Erreur lors de l\'archivage du rapport', 'error');
    }
  }, [selectedReport, isInsertMode, stopInsertMode, showNotification, setReports, archiveReportAPI]);

  const handleDeleteReport = useCallback(async (reportId) => {
    // Find the report to get its name for the confirmation message
    const reportToDelete = reports.find(r => r.id === reportId);
    if (!reportToDelete) return;

    const confirmMessage = `Êtes-vous sûr de vouloir supprimer définitivement le rapport "${formatReportTypeName(reportToDelete.reportType)}" ?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await reportService.deleteReport(reportId);
      
      // Remove the report from the list
      setReports((prevReports) => {
        const updatedReports = prevReports.filter(r => r.id !== reportId);
        saveReportsToLocalStorage(updatedReports);
        return updatedReports;
      });
      
      // Clear selection if this report was selected
      if (selectedReport && selectedReport.id === reportId) {
        setSelectedReport(null);
        setMdReport('');
        localStorage.removeItem('selectedReportId');
      }
      
      showNotification('Rapport supprimé définitivement', 'success');
    } catch (error) {
      log.error('Error deleting report:', error);
      showNotification('Erreur lors de la suppression du rapport', 'error');
    }
  }, [reports, selectedReport, showNotification, setReports]);

  const handleContextMenu = useCallback((e, reportId) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      reportId: reportId
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({ show: false, x: 0, y: 0, reportId: null });
  }, []);

  const handleSendToServer = useCallback(async () => {
    if (!selectedReport) return;
    
    // Check for uncertainty markers and show confirmation
    if (!confirmUncertaintyAction('l\'améliorer')) return;
    
    if (isInsertMode) {
      stopInsertMode();
    }
    
    isSendingRef.current = true;
    handleSave();
    
    try {
      await improveReportWithAI(selectedReport.id, mdReport, selectedReport.structuredData);
      
      showNotification(`${formatReportTypeName(selectedReport.reportType)} amélioré.e avec succès`, 'success');
      
      const updatedReport = normalizeReport(selectedReport);
      updatedReport.isCorrected = true;
      
      setReports((prevReports) => {
        const newReports = prevReports.map((r) =>
          r.id === updatedReport.id ? updatedReport : r
        );
        saveReportsToLocalStorage(newReports);
        return newReports;
      });
      
      setSelectedReport(updatedReport);
    } catch (error) {
      log.error('Erreur lors de la sauvegarde du rapport:', error);
      showNotification('Erreur lors de la sauvegarde du rapport', 'error');
    } finally {
      isSendingRef.current = false;
    }
  }, [handleSave, selectedReport, mdReport, stopInsertMode, showNotification, setReports, improveReportWithAI, confirmUncertaintyAction]);

  const handleChangeReportType = useCallback(async (newType) => {
    if (!selectedReport) return;
    
    try {
      await changeReportType(newType, selectedReport.transcription, selectedReport.annex);
      showNotification(`Nouvelle version comme ${formatReportTypeName(newType)} en cours de rédaction...`, 'success');
      await handleRefresh();
    } catch (err) {
      log.error('Erreur changeReportType :', err);
      showNotification('Impossible de changer le modèle.', 'warning');
    } finally {
      setShowReportTypeOverlay(false);
    }
  }, [selectedReport, showNotification, changeReportType]);

  // Handler for generating prescription from consultation/suivi
  const [isGeneratingPrescription, setIsGeneratingPrescription] = useState(false);
  
  const handleGeneratePrescription = useCallback(async () => {
    if (!selectedReport) return;
    
    // Check for uncertainty markers and show confirmation
    if (!confirmUncertaintyAction('générer une ordonnance')) return;
    
    setIsGeneratingPrescription(true);
    
    try {
      //get the reportType to know if enterprise or not
      const selectedReportType = selectedReport.reportType;
      const enterpriseName = selectedReportType.split('_')[1];
      const userEnterprises = profile?.enterpriseNames
      const isEnterprise = userEnterprises.includes(enterpriseName);
      let reportType = 'ordonnance';
      if (isEnterprise) {
        reportType = 'ordonnance' + '_' + enterpriseName;
      }

      // Get the current report content (either corrected or original)
      const currentContent = mdReport;
      
      // Combine the instruction with the current report content and annex
      const prescriptionRequest = `Merci de d'extraire les informations pour générer une ordonnance à partir de ce rapport de consultation\n\nReprend les traitements actuels du patient (si existants) et les nouveaux mentionnés dans le rapport (si existants). Si un traitement remplace un autre, met le nouveau traitement avec en remarque (remplace XX).\n\n"""${currentContent}"""`;
      
      // Generate the prescription using the report service
      const prescriptionReport = await reportService.generateReport(
        reportType,
        prescriptionRequest,
        selectedReport.annex || null,
        isEnterprise
      );



      showNotification('Ordonnance générée avec succès', 'success');

      // Refresh the reports list to show the newly created prescription
      await handleRefresh();
    } catch (error) {
      log.error('Erreur lors de la génération de l\'ordonnance:', error);
      showNotification('Erreur lors de la génération de l\'ordonnance', 'error');
    } finally {
      setIsGeneratingPrescription(false);
    }
  }, [selectedReport, mdReport, showNotification, setReports, confirmUncertaintyAction]);

  // Handler for creating enterprise example
  const handleCreateEnterpriseExample = useCallback(async () => {
    if (!selectedReport) return;
    
    // Check for uncertainty markers and show confirmation
    if (!confirmUncertaintyAction('créer un exemple organisation')) return;
    
    setEnterpriseExampleLoading(true);
    
    try {
      // Get user's enterprises
      const enterprises = await enterpriseService.getMyEnterprises();
      
      if (enterprises.length === 0) {
        showNotification('Aucune organisation trouvée', 'warning');
        return;
      }

      setAvailableEnterprises(enterprises);
      setAvailableEnterpriseReportTypes([]); // will be fetched when enterprise is selected
      setSelectedEnterpriseId(null);
      setSelectedReportType(null);
      setShowEnterpriseSelectionModal(true);
    } catch (error) {
      log.error('Error loading enterprises:', error);
      showNotification('Erreur lors du chargement des organisations', 'error');
    } finally {
      setEnterpriseExampleLoading(false);
    }
  }, [selectedReport, profile, showNotification, confirmUncertaintyAction]);

  // Fetch enterprise report types when an enterprise is selected
  useEffect(() => {
    const fetchEnterpriseTypes = async () => {
      if (!selectedEnterpriseId) {
        setAvailableEnterpriseReportTypes([]);
        return;
      }
      setSelectedReportType(null);
      setEnterpriseReportTypesLoading(true);
      try {
        const types = await enterpriseAdminService.getEnterpriseReportTypes(selectedEnterpriseId);
        // Map to simple objects with id and name for the picker
        const list = Array.isArray(types) ? types.map(t => ({ id: t.id, name: t.name })) : [];
        setAvailableEnterpriseReportTypes(list);
      } catch (err) {
        log.error('Error loading enterprise report types:', err);
        setAvailableEnterpriseReportTypes([]);
        showNotification('Erreur lors du chargement des modèles de l\'organisation', 'error');
      } finally {
        setEnterpriseReportTypesLoading(false);
      }
    };
    fetchEnterpriseTypes();
  }, [selectedEnterpriseId]);

  // Handler for creating admin default example
  const handleCreateAdminDefaultExample = useCallback(async () => {
    if (!selectedReport) return;
    if (!profile?.isAdmin) return;
    
    // Check for uncertainty markers and show confirmation
    if (!confirmUncertaintyAction('créer un exemple par défaut')) return;

    setAdminExampleLoading(true);
    try {
      const exampleData = {
        reportType: selectedReport.reportType,
        transcription: selectedReport.transcription,
        correctedContent: selectedReport.correctedContent || mdReport,
        annex: selectedReport.annex || undefined,
        specialty: profile?.specialty || 'cardiologie'
      };

      await adminService.createDefaultExample(exampleData);
      showNotification('Exemple par défaut créé avec succès', 'success');
    } catch (error) {
      log.error('Error creating default example (admin):', error);
      showNotification("Erreur lors de la création de l'exemple par défaut", 'error');
    } finally {
      setAdminExampleLoading(false);
    }
  }, [selectedReport, mdReport, profile, showNotification, confirmUncertaintyAction]);

  // Helper function to create the example
  const createEnterpriseExample = useCallback(async (enterpriseId, reportType) => {
    if (!selectedReport || !enterpriseId || !reportType) return;
    
    // Check for uncertainty markers and show confirmation
    if (!confirmUncertaintyAction('créer cet exemple organisation')) return;
    
    try {
      const exampleData = {
        reportType: reportType,
        transcription: selectedReport.transcription,
        correctedContent: selectedReport.correctedContent || mdReport,
        annex: selectedReport.annex || undefined
      };
      
      await enterpriseAdminService.createEnterpriseExample(enterpriseId, exampleData);
      showNotification('Exemple organisation créé avec succès', 'success');
      setShowEnterpriseSelectionModal(false);
      setSelectedEnterpriseId(null);
      setSelectedReportType(null);
    } catch (error) {
      log.error('Error creating enterprise example:', error);
      showNotification('Erreur lors de la création de l\'exemple', 'error');
    }
  }, [selectedReport, mdReport, showNotification, hasUncertaintyMarkers]);

  // Handle status checking for processing reports
  const handleStatusCheck = useCallback(async (report) => {
    const reportId = report.id;
    
    // Add to checking set
    setStatusCheckingReports(prev => new Set(prev).add(reportId));
    
    // Minimum 500ms loading time
    const startTime = Date.now();
    const minimumLoadingTime = 500;
    
    try {
      const updatedReport = await reportService.getReport(reportId);
      
      // Ensure minimum loading time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      // Update reports state
      setReports((prevReports) => {
        const updatedReports = prevReports.map((r) =>
          r.id === reportId ? updatedReport : r
        );
        saveReportsToLocalStorage(updatedReports);
        return updatedReports;
      });
      
      // Set success result
      setStatusCheckResults(prev => new Map(prev).set(reportId, {
        type: 'success',
        message: updatedReport.status === 'completed' ? '' : 'Toujours en cours...',
        timestamp: new Date()
      }));
      
    } catch (error) {
      log.error('Error checking report status:', error);
      
      // Ensure minimum loading time even on error
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      // Set error result
      setStatusCheckResults(prev => new Map(prev).set(reportId, {
        type: 'error',
        message: 'Erreur lors de la vérification',
        timestamp: new Date()
      }));
      
    } finally {
      // Remove from checking set
      setStatusCheckingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    }
  }, [setReports]);

  // Report selection
  const handleReportClick = useCallback(async (report) => {
    // If report is processing, check status instead of opening
    if (isReportStatusCheckable(report)) {
      await handleStatusCheck(report);
      return;
    }
    
    // Only proceed with normal click handling for non-processing reports
    if (!canReportBeOpened(report)) {
      return;
    }

    // Save current report before switching to avoid data loss
    if (selectedReport && selectedReport.id !== report.id) {
      // Clear any pending auto-save timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
      // Save immediately
      handleSave();
    }

    const content = getReportContent(report);
    setMdReport(content || '');
    
    // Detect [?] markers
    detectUncertaintyMarkers(content || '');
    
    // Ensure structuredData is initialized for ETT and Echo reports
    const reportToSelect = (report.reportType.startsWith('ett') || report.reportType.startsWith('echo')) && !report.structuredData
      ? { ...report, structuredData: {} }
      : report;
    
    setSelectedReport(reportToSelect);
    stopInsertMode();
    setShowTextOptions(false);
    setSections([]);
    setCopiedSections([]);

    // Persist selected report ID for restoration after navigation
    localStorage.setItem('selectedReportId', report.id);

    // Mark report as read if it's unread
    if (!report.isRead) {
      try {
        await reportService.markReportAsRead(report.id);
        
        // Update local state to mark as read
        setReports((prevReports) => {
          const updatedReports = prevReports.map((r) => {
            if (r.id === report.id) {
              const updatedReport = normalizeReport(r);
              updatedReport.isRead = true;
              return updatedReport;
            }
            return r;
          });
          
          saveReportsToLocalStorage(updatedReports);
          return updatedReports;
        });
      } catch (error) {
        log.error('Error marking report as read:', error);
      }
    }
  }, [stopInsertMode, setReports, handleStatusCheck, selectedReport, handleSave, detectUncertaintyMarkers]);

  // Other handlers remain the same...
  const handleInsertion = useCallback(() => {
    if (!isInsertMode) {
      if (!selectedReport) return;
      
      // Check for uncertainty markers and show confirmation
      if (!confirmUncertaintyAction('activer le mode insertion')) return;
      
      if (!isElectron) setIsInsertMode(true); //dont show the insertions button on the electron app
    } else {
      stopInsertMode();
      return;
    }

    handleSave();
    const newSecs = parseReportSections(mdReport);
    // Ensure sections have string properties
    const sanitizedSecs = newSecs.map(sec => {
      // Debug check
      if (sec && typeof sec === 'object' && (sec.basal_anterior !== undefined || sec.apical_anterior !== undefined)) {
        log.error('ERROR: Bullseye data found in sections!', sec);
        return null;
      }
      return {
        title: (sec.title && typeof sec.title === 'string') ? sec.title : '',
        content: (sec.content && typeof sec.content === 'string') ? sec.content : ''
      };
    }).filter(Boolean); // Remove any null entries

    // Add destinataire as first section if it exists
    if (selectedReport.destinataire) {
      sanitizedSecs.unshift({
        title: 'Destinataire',
        content: selectedReport.destinataire
      });
    }
    setSections(sanitizedSecs);

    setShowExportMenu(false);
    setShowTextOptions(false);

    if (window.electronAPI) {
      window.electronAPI.storeCurrentReportId(selectedReport.id);
      window.electronAPI.startSectionInsertion({
        name: selectedReport.reportType,
        sections: sanitizedSecs
      });
    }
  }, [selectedReport, mdReport, isInsertMode, stopInsertMode, handleSave, hasUncertaintyMarkers]);

  const handleCopy = useCallback(() => {
    if (!selectedReport) return;
    
    // Check for uncertainty markers and show confirmation
    if (!confirmUncertaintyAction('le copier')) return;
    
    copyTextToClipboard(mdReport).then(() => {
      showNotification(`${formatReportTypeName(selectedReport.reportType)} copié.`, 'info');
    }).catch(err => log.error('Erreur copie :', err));
  }, [selectedReport, mdReport, showNotification, hasUncertaintyMarkers]);

  const handleUndo = useCallback(() => {
    if (!selectedReport) return;
    const content = getReportContent(selectedReport);
    setMdReport(content);
    stopInsertMode();
    setSections([]);
    setCopiedSections([]);
  }, [selectedReport, stopInsertMode]);

  // Programmatic Bullseye image generation (no UI interaction)
  const generateBullseyeImage = useCallback(async () => {
    try {
      // For Word export, use higher scale for better quality
      // The scale will be reduced by outputDownscaleFactor to maintain size
      const dataUrl = await renderBullseyeImageUnified(
        selectedReport?.structuredData || {},
        { scale: 3, outputDownscaleFactor: 0.5 }
      );
      return dataUrl;
    } catch (error) {
      log.error('❌ Erreur lors de la génération de l\'image Bullseye:', error);
      return null;
    }
  }, [selectedReport?.structuredData]);


  const handleExportWord = useCallback(async (mode = 'auto', formatOverride) => {
    // Check for uncertainty markers and show confirmation
    if (!confirmUncertaintyAction('l\'exporter')) return;
    
    let bullseyeOpened = false;
    let wasShowingText = false;
    
    // Check if we have structured data and if we're currently showing text
    const hasStructuredData = selectedReport?.structuredData && canRenderBullseyeImage(selectedReport.structuredData);
    if (hasStructuredData && !showSchema) {
      // Remember we were showing text and temporarily switch to schema
      wasShowingText = true;
      setShowSchema(true);
      // Wait for React to render the schema
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    try {
      showNotification('Génération du document en cours...', 'info');
      
        // Parser les sections du rapport (liste [{ title, content }])
        const parsedSections = parseReportSections(mdReport);
        
        // Sanitize sections to ensure string properties
        const sanitizedSections = parsedSections.map(sec => ({
          title: (sec.title && typeof sec.title === 'string') ? sec.title : '',
          content: (sec.content && typeof sec.content === 'string') ? sec.content : ''
        }));

        // Add destinataire as first section if it exists
        if (selectedReport?.destinataire) {
          sanitizedSections.unshift({
            title: 'Destinataire',
            content: selectedReport.destinataire
          });
        }

        // Nouveau format d'export attendu par le backend
        const isEnterprise = profile?.reportTypeFavorites?.isEnterprise?.(selectedReport?.reportType) || false;
        
        const reportType = selectedReport?.reportType;
        // Find reportTypeId from profile
        let reportTypeId = null;
        if (profile?.reportTypeFavorites?.getIdForType && reportType) {
          reportTypeId = profile.reportTypeFavorites.getIdForType(reportType);
        }
        // Prepend destinataire to report text if it exists
        let reportText = mdReport || '';
        if (selectedReport?.destinataire) {
          reportText = `Destinataire\n${selectedReport.destinataire}\n${reportText}`;
        }
        const exportData = {
          reportType: selectedReport?.reportType || '',
          reportTypeId: reportTypeId || '',
          isEnterprise,
          report: reportText,
          sections: Array.isArray(sanitizedSections) ? sanitizedSections : [],
          format: formatOverride === 'pdf' ? 'pdf' : 'docx'
        };

        // Détection automatique des tableaux basée sur le contenu (sans se fier au titre)
        const parseTableContent = (raw) => {
          const lines = (raw || '')
            .split(/\r?\n/)
            .map(l => l.trim())
            .filter(l => l.length > 0);
          if (lines.length < 1) return null;
          const splitLine = (line) => line.split(/\s*---\s*/).map(cell => cell.trim());
          const header = splitLine(lines[0]);
          const rows = lines.slice(1).map(splitLine);
          // Le contenu ressemble à un tableau seulement si on a au moins 2 colonnes
          if (!header || header.length < 2) return null;
          // Toutes les lignes doivent avoir le même nombre de colonnes
          const consistent = rows.every(r => r.length === header.length);
          if (!consistent) return null;
          // On considère valide même sans lignes (header seul)
          return { header, rows };
        };

        const usedNames = new Set();
        const uniqueName = (base) => {
          let name = base || 'examTable';
          if (!usedNames.has(name)) { usedNames.add(name); return name; }
          let i = 2;
          while (usedNames.has(`${name}${i}`)) i += 1;
          const finalName = `${name}${i}`;
          usedNames.add(finalName);
          return finalName;
        };

        const tables = [];
        const sectionsWithoutTables = [];
        sanitizedSections.forEach((sec) => {
          const parsed = parseTableContent(sec.content);
          if (!parsed) {
            sectionsWithoutTables.push(sec);
            return;
          }
          const { header, rows } = parsed;
          const numCols = Math.max(1, header.length);
          const totalTwips = 9500; // largeur approx. exploitable; distribution simple
          const colWidth = Math.floor(totalTwips / numCols);
          const widthsTwips = new Array(numCols).fill(colWidth);
          const rawTitle = (sec.title || '').toString().trim();
          const baseName = rawTitle || 'examTable';
          const name = uniqueName(baseName);
          tables.push({
            name,
            header,
            rows,
            borders: true,
            widthsTwips
          });
        });

        exportData.sections = sectionsWithoutTables;
        if (tables.length > 0) exportData.tables = tables;
      
      // Si le rapport contient des données structurées, ajouter l'image du diagramme approprié
      if (selectedReport?.structuredData && canRenderBullseyeImage(selectedReport.structuredData)) {

        
        // Mémoriser si on a ouvert le bullseye
        const bullseyeWrapperBefore = document.getElementById("bullseye-wrapper");
        bullseyeOpened = !bullseyeWrapperBefore;
        
        const bullseyeImage = await generateBullseyeImage();
        if (bullseyeImage) {

          exportData.image = bullseyeImage; // Image en base64
        } else {

        }
      } else {

      }
      

      
      const exportResult = await wordService.exportToWord(exportData);
      const blob = exportResult?.blob || exportResult; // back-compat if service returned blob directly
      
      // Always use the filename from the service if available
      const serviceFileName = exportResult?.fileName;
      
      // Ouvrir directement dans l'application par défaut (Electron) si possible
      if (mode !== 'download' && exportData.format === 'docx' && blob && blob instanceof Blob && isElectron) {
        try {
          const blobToBase64 = (b) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result || '';
              const base64 = typeof result === 'string' ? result.split(',')[1] : '';
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(b);
          });

          const base64Data = await blobToBase64(blob);
          const filename = serviceFileName || `${selectedReport?.reportType || 'export'}_${Date.now()}.docx`;

          // If a default export dir exists (state or fetched), save there, then open from there
          let effectiveDefaultDir = defaultExportDir;
          if (!effectiveDefaultDir && isElectron && window.electronAPI?.getDefaultExportDir) {
            try {
              const fetched = await window.electronAPI.getDefaultExportDir();
              if (typeof fetched === 'string' && fetched.length > 0) {
                effectiveDefaultDir = fetched;
                setDefaultExportDir(fetched);
              }
            } catch (_) {}
          }
          if (effectiveDefaultDir && window.electronAPI?.saveToDefaultExportDir && window.electronAPI?.openFilePath) {
            const now = new Date();
            const monthNames = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
            const dayNames = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];
            const monthName = monthNames[now.getMonth()];
            const weekdayName = dayNames[now.getDay()];
            const day = String(now.getDate()).padStart(2, '0');
            const subDir = sortMonthDay ? `${monthName} - voxia/${day} - ${weekdayName}` : '';
            const saveRes = await window.electronAPI.saveToDefaultExportDir({ fileName: filename, base64Data: base64Data, subDir });
            if (saveRes?.success && saveRes?.path) {
              const openRes = await window.electronAPI.openFilePath(saveRes.path);
              if (!openRes?.success) {
                log.error('Open file failed:', openRes?.error);
              }
              showNotification('Document enregistré dans le dossier par défaut et ouvert', 'success');
              if (wasShowingText) {
                setShowSchema(false);
              }
              if (bullseyeOpened) {
                const closeButton = document.querySelector('.report-type-overlay button[variant="secondary"]') ||
                                   document.querySelector('[data-testid="close-bullseye"]') ||
                                   Array.from(document.querySelectorAll('button')).find(btn => 
                                     btn.querySelector('.fa-times') && 
                                     btn.closest('[role="dialog"], .popover, .overlay')
                                   );
                if (closeButton) {
                  closeButton.click();
                }
              }
              return blob;
            } else if (saveRes && saveRes.error) {
              log.error('Save to default dir failed:', saveRes.error);
            }
          }

          // Fallback: save to Downloads folder then open (never use temp)
          if (window.electronAPI?.saveFileWithDialog) {
            // Use Downloads folder as default directory instead of temp
            const result = await window.electronAPI.saveFileWithDialog(base64Data, filename, [
              { name: 'Word document', extensions: ['docx'] }
            ], true); // Pass useDownloads=true to use Downloads folder
            if (result?.success && result?.path) {
              // Open the saved file
              const openRes = await window.electronAPI.openFilePath(result.path);
              if (!openRes?.success) {
                log.error('Open file failed:', openRes?.error);
              }
              showNotification('Document enregistré dans le dossier Téléchargements et ouvert', 'success');
              if (wasShowingText) {
                setShowSchema(false);
              }
              if (bullseyeOpened) {
                const closeButton = document.querySelector('.report-type-overlay button[variant="secondary"]') ||
                                   document.querySelector('[data-testid="close-bullseye"]') ||
                                   Array.from(document.querySelectorAll('button')).find(btn => 
                                     btn.querySelector('.fa-times') && 
                                     btn.closest('[role="dialog"], .popover, .overlay')
                                   );
                if (closeButton) {
                  closeButton.click();
                }
              }
              return blob;
            } else if (result && !result.canceled) {
              log.error('Save to Downloads failed:', result?.error);
            }
          }
        } catch (e) {
          log.error('Erreur ouverture via Electron, fallback au téléchargement:', e);
        }
      }

      // Electron explicit download via save dialog
      if (blob && blob instanceof Blob && isElectron && mode === 'download' && window.electronAPI?.saveFileWithDialog) {
        const blobToBase64 = (b) => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result || '';
            const base64 = typeof result === 'string' ? result.split(',')[1] : '';
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(b);
        });
        const base64Data = await blobToBase64(blob);
        const isPdf = exportData.format === 'pdf';
        const filename = serviceFileName || `${selectedReport?.reportType || 'export'}_${Date.now()}.docx`;
        const filters = isPdf
          ? [{ name: 'PDF', extensions: ['pdf'] }]
          : [{ name: 'Word document', extensions: ['docx'] }];
        const res = await window.electronAPI.saveFileWithDialog(base64Data, filename, filters);
        if (res?.success) {
          showNotification(isPdf ? 'PDF enregistré' : 'Document enregistré', 'success');
        } else if (!res?.canceled) {
          showNotification('Échec de l\'enregistrement', 'error');
        }
        // Close bullseye if needed
        if (bullseyeOpened) {
          const closeButton = document.querySelector('.report-type-overlay button[variant="secondary"]') ||
                             document.querySelector('[data-testid="close-bullseye"]') ||
                             Array.from(document.querySelectorAll('button')).find(btn => 
                               btn.querySelector('.fa-times') && 
                               btn.closest('[role="dialog"], .popover, .overlay')
                             );
          if (closeButton) {
            closeButton.click();
          }
        }
        if (wasShowingText) {
          setShowSchema(false);
        }
        return blob;
      }

      // Créer le lien de téléchargement directement avec le blob (fallback navigateur / non-Electron)
      if (blob && blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = serviceFileName || `${selectedReport?.reportType || 'export'}_${Date.now()}.${exportData.format === 'pdf' ? 'pdf' : 'docx'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showNotification(exportData.format === 'pdf' ? 'Export PDF terminé.' : 'Export en Word terminé.', 'success');
        
        // Fermer le bullseye si on l'avait ouvert
        if (bullseyeOpened) {
          const closeButton = document.querySelector('.report-type-overlay button[variant="secondary"]') ||
                             document.querySelector('[data-testid="close-bullseye"]') ||
                             Array.from(document.querySelectorAll('button')).find(btn => 
                               btn.querySelector('.fa-times') && 
                               btn.closest('[role="dialog"], .popover, .overlay')
                             );
          if (closeButton) {
            closeButton.click();
          }
        }
        
        // Switch back to text view if we temporarily showed schema
        if (wasShowingText) {
          setShowSchema(false);
        }
        
        return blob;
      } else {
        showNotification('Aucun fichier retourné par l\'export', 'warning');
        
        // Switch back to text view if we temporarily showed schema
        if (wasShowingText) {
          setShowSchema(false);
        }
        
        // Fermer le bullseye en cas d'échec aussi
        if (bullseyeOpened) {
          const closeButton = document.querySelector('.report-type-overlay button[variant="secondary"]') ||
                             document.querySelector('[data-testid="close-bullseye"]') ||
                             Array.from(document.querySelectorAll('button')).find(btn => 
                               btn.querySelector('.fa-times') && 
                               btn.closest('[role="dialog"], .popover, .overlay')
                             );
          if (closeButton) {
            closeButton.click();
          }
        }
      }
    } catch (error) {
      log.error('Export error:', error);
      showNotification('Erreur lors de l\'export', 'error');
      
      // Switch back to text view if we temporarily showed schema
      if (wasShowingText) {
        setShowSchema(false);
      }
      
      // Fermer le bullseye en cas d'erreur
      if (bullseyeOpened) {
        const closeButton = document.querySelector('.report-type-overlay button[variant="secondary"]') ||
                           document.querySelector('[data-testid="close-bullseye"]') ||
                           Array.from(document.querySelectorAll('button')).find(btn => 
                             btn.querySelector('.fa-times') && 
                             btn.closest('[role="dialog"], .popover, .overlay')
                           );
        if (closeButton) {
          closeButton.click();
        }
      }
      
      throw error;
    }
  }, [mdReport, selectedReport, showNotification, generateBullseyeImage, showSchema, setShowSchema]);

  // Export sections to CSV/Excel
  const handleExportSectionsCSV = useCallback(() => {
    // Check for uncertainty markers and show confirmation
    if (!confirmUncertaintyAction('exporter les sections en CSV')) return;
    
    try {
      const type = selectedReport?.reportType || 'rapport';
      const filename = `sections_${type}_${Date.now()}.csv`;
      downloadSectionsCSVFromText(mdReport || '', filename, ',');
      showNotification('Sections exportées en CSV', 'success');
    } catch (error) {
      log.error('Export CSV error:', error);
      showNotification("Erreur lors de l'export CSV", 'error');
    }
  }, [mdReport, selectedReport, showNotification, hasUncertaintyMarkers]);

  const handlePrint = useCallback(() => {
    if (!selectedReport) return;
    
    // Check for uncertainty markers and show confirmation
    if (!confirmUncertaintyAction('l\'imprimer')) return;
    
    // Create a temporary iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <html>
        <head>
          <title>${selectedReport.reportType} - ${formatDate(selectedReport.createdAt)}</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; }
            h1 { color: #333; }
            pre { white-space: pre-wrap; word-wrap: break-word; }
          </style>
        </head>
        <body>
          <h1>${formatReportTypeName(selectedReport.reportType)}</h1>
          <p>Date: ${formatDate(selectedReport.createdAt)}</p>
          <hr />
          <pre>${mdReport}</pre>
        </body>
      </html>
    `);
    doc.close();
    
    // Wait for content to load then print
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 100);
      }, 100);
    };
  }, [selectedReport, mdReport, hasUncertaintyMarkers]);

  const handleSendEmail = useCallback(() => {
    // Check for uncertainty markers and show confirmation
    if (!confirmUncertaintyAction('l\'envoyer par email')) return;
    
    const getMailtoLink = () => {
      const structuredEmail = parseMailSections(mdReport);
      const email = structuredEmail.Email || '';
      const subject = structuredEmail.Objet || '';
      const body = structuredEmail.Corps || '';
      return createMailtoLink(email, subject, body);
    };

    const mailLink = getMailtoLink();
    if (!mailLink.includes('mailto:') || mailLink === 'mailto:?subject=&body=') {
      showNotification('Impossible de détecter Email: ou Objet: dans ce texte.', 'warning');
      return;
    }
    window.open(mailLink, '_blank');
  }, [mdReport, showNotification, hasUncertaintyMarkers]);


  const handleToggleExportMenu = useCallback(() => {
    setShowExportMenu(prev => !prev);
    setShowTextOptions(false);
  }, []);

  const handleToggleTextOptions = useCallback(() => {
    setShowTextOptions(prev => !prev);
    setShowExportMenu(false);
    setShowReportTypeOverlay(false);
  }, []);

  const handleToggleReportTypeOverlay = useCallback(() => {
    setShowReportTypeOverlay(prev => !prev);
    setShowExportMenu(false);
    setShowTextOptions(false);
  }, []);

  const handleToggleAnnexPanel = useCallback(() => {
    setShowAnnexPanel(prev => !prev);
    setShowExportMenu(false);
    setShowTextOptions(false);
    setShowReportTypeOverlay(false);
  }, []);

  const handleToggleDestinatairePanel = useCallback(() => {
    setShowDestinatairePanel(prev => {
      if (!prev && selectedReport) {
        setEditedDestinataire(selectedReport.destinataire || '');
      }
      return !prev;
    });
    setShowExportMenu(false);
    setShowTextOptions(false);
    setShowReportTypeOverlay(false);
  }, [selectedReport]);

  const handleSaveDestinataire = useCallback(async (valueToSave) => {
    if (!selectedReport) return;
    const destinataireValue = valueToSave !== undefined ? valueToSave : editedDestinataire;
    try {
      await reportService.updateReport(selectedReport.id, { destinataire: destinataireValue });
      const updatedReport = normalizeReport(selectedReport);
      updatedReport.destinataire = destinataireValue;
      setReports(prev => {
        const updatedArr = prev.map(r => r.id === updatedReport.id ? updatedReport : r);
        saveReportsToLocalStorage(updatedArr);
        return updatedArr;
      });
      setSelectedReport(updatedReport);
    } catch (error) {
      log.error('Error saving destinataire:', error);
      showNotification('Erreur lors de l\'enregistrement', 'error');
    }
  }, [selectedReport, editedDestinataire, setReports, showNotification]);

  // Debounced auto-save for destinataire
  const destinataireSaveTimeoutRef = useRef(null);
  const handleDestinataireChange = useCallback((e) => {
    const newValue = e.target.value;
    setEditedDestinataire(newValue);

    // Clear previous timeout
    if (destinataireSaveTimeoutRef.current) {
      clearTimeout(destinataireSaveTimeoutRef.current);
    }

    // Debounce save by 500ms
    destinataireSaveTimeoutRef.current = setTimeout(() => {
      handleSaveDestinataire(newValue);
    }, 500);
  }, [handleSaveDestinataire]);



  const handleExportButtonClick = useCallback(async () => {
    if (!selectedReport) return;
    
    setShowTextOptions(false);
    
    try {
      await handleExportWord();
    } catch (error) {
      log.error('Error during direct export:', error);
      showNotification('Erreur lors de l\'export', 'error');
    }
  }, [selectedReport, mdReport, handleExportWord, showNotification, hasUncertaintyMarkers]);

  const handleExportOpenClick = useCallback(async () => {
    if (!selectedReport) return;
    setShowTextOptions(false);
    try {
      await handleExportWord('open');
    } catch (error) {
      log.error('Error during open export:', error);
      showNotification('Erreur lors de l\'ouverture du document', 'error');
    }
  }, [selectedReport, handleExportWord, showNotification]);

  const handleExportDownloadClick = useCallback(async () => {
    if (!selectedReport) return;
    setShowTextOptions(false);
    try {
      await handleExportWord('download');
    } catch (error) {
      log.error('Error during download export:', error);
      showNotification('Erreur lors du téléchargement', 'error');
    }
  }, [selectedReport, handleExportWord, showNotification]);

  const handleSetDefaultFolder = useCallback(async () => {
    if (!isElectron) return;
    try {
      if (window.electronAPI?.chooseDefaultExportDir) {
        const res = await window.electronAPI.chooseDefaultExportDir();
        if (res?.success && res?.path) {
          setDefaultExportDir(res.path);
          showNotification('Dossier d\'export par défaut défini', 'success');
        }
      }
    } catch (error) {
      log.error('Error setting default folder:', error);
      showNotification('Erreur lors de la définition du dossier', 'error');
    }
  }, [showNotification]);

  // Toggle sorting exports into date-based subfolders (Electron only)
  const handleToggleSortByDate = useCallback(() => {
    setSortMonthDay(prev => {
      const next = !prev;
      try { localStorage.setItem('export.sortMonthDay', next ? '1' : '0'); } catch (_) {}
      showNotification(`Trier par date: ${next ? 'Oui' : 'Non'}`, 'info');
      return next;
    });
  }, [showNotification]);


  // Removed PDF export shortcut per request
  const handleSaveOpenedAsPdf = useCallback(async () => {
    showNotification('Export PDF désactivé pour le moment', 'warning');
  }, [showNotification]);

  const handleBullseyeChange = useCallback((newData) => {
    if (!selectedReport) return;
    
    
    // Create updated report with new structuredData
    const updatedReport = {
      ...selectedReport,
      structuredData: newData
    };
    
    // Update local state immediately
    setSelectedReport(updatedReport);
    
    // Update reports array and save to localStorage immediately
    setReports(prevReports => {
      const updatedReports = prevReports.map(report => 
        report.id === selectedReport.id ? updatedReport : report
      );
      saveReportsToLocalStorage(updatedReports);
      return updatedReports;
    });
    
  }, [selectedReport, setReports]);

  // Handle sidebar indicator click
  const handleSidebarIndicatorClick = useCallback(() => {
    if (isCapacitor) {
      // On Capacitor, clear selected report instead of showing sidebar
      setSelectedReport(null);
      setMdReport('');
      localStorage.removeItem('selectedReportId');
    } else {
      // On other platforms, show sidebar
      setIsSidebarVisible(true);
    }
  }, [isCapacitor]);

  // ===== EFFECTS =====
  
  // Load persisted dateFilter from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('dateFilter');
      const allowed = new Set(Object.values(DATE_FILTERS));
      if (stored && allowed.has(stored)) {
        if (stored !== dateFilter) {
          setDateFilter(stored);
        }
      }
    } catch (e) {
      log.error('[ReportList] Error loading dateFilter from localStorage:', e);
    } finally {
      hasHydratedDateFilterRef.current = true;
    }
  }, []);

  // Persist dateFilter to localStorage when it changes (after hydration)
  useEffect(() => {
    if (!hasHydratedDateFilterRef.current) return;
    try {
      localStorage.setItem('dateFilter', dateFilter);
    } catch (e) {
      // ignore
    }
  }, [dateFilter]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowExportMenu(false);
      setShowTextOptions(false);
      setShowReportTypeOverlay(false);
      setContextMenu({ show: false, x: 0, y: 0, reportId: null });
    };

    if (contextMenu.show) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu.show]);
  
  

  // Filter reports when filters change
  useEffect(() => {
    // If advanced search is active, override filtered reports with results
    if (isAdvancedSearchActive) {
      setFilteredReports(advancedSearchResults);
      // Ensure selected report is part of results; otherwise clear selection
      if (selectedReport && !advancedSearchResults.find(r => r.id === selectedReport.id)) {
        setSelectedReport(null);
        setMdReport('');
        localStorage.removeItem('selectedReportId');
      }
      return;
    }

    let filtered = reports;
    
    // Apply date filter
    filtered = filterReportsByDate(filtered, dateFilter);
    
    // Apply archive filter
    filtered = filtered.filter((r) => {
      if (showArchived) {
        return r.isArchived === true
      } else {
        return r.isArchived !== true;
      }
    });
    
    // Apply search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) => {
          const content = getReportContent(r);
          const displayName = r.reportType
          return displayName.toLowerCase().includes(q) || content.toLowerCase().includes(q);
        }
      );
    }
    
    // Apply type filter
    if (reportTypeFilter) {
      filtered = filtered.filter((r) => r.reportType === reportTypeFilter);
    }
    
    // Note: enterprise scope and generator filtering now handled via advanced search
    
    setFilteredReports(filtered);
    
    // Check if selected report is still in filtered results
    if (selectedReport && !filtered.find(r => r.id === selectedReport.id)) {
      setSelectedReport(null);
      setMdReport('');
      localStorage.removeItem('selectedReportId');
    }
  }, [reports, searchQuery, reportTypeFilter, dateFilter, showArchived, selectedReport, profile, isAdvancedSearchActive, advancedSearchResults]);

  // Handle responsive sidebar
  // When recorder panel is open, use a larger breakpoint to collapse sidebar earlier
  useEffect(() => {
    const handleResize = () => {
      // Add panel width to breakpoint when panel is open (450px panel + some buffer)
      const effectiveBreakpoint = isRecorderPanelOpen ? SMALL_SCREEN_BREAKPOINT + 500 : SMALL_SCREEN_BREAKPOINT;
      const small = window.innerWidth < effectiveBreakpoint;
      setIsSmallScreen(small);
      if (!small) {
        setIsSidebarVisible(true);
      } else if (!selectedReport) {
        setIsSidebarVisible(true);
      } else {
        setIsSidebarVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [selectedReport, isRecorderPanelOpen]);

  // Auto-refresh on mount (web only)
  useEffect(() => {
    handleRefreshWithCooldown();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore selected report from localStorage on mount
  useEffect(() => {
    const restoreSelectedReport = () => {
      try {
        const storedSelectedReportId = localStorage.getItem('selectedReportId');
        if (storedSelectedReportId && reports.length > 0 && !selectedReport) {
          const reportToSelect = reports.find(r => r.id === storedSelectedReportId);
          if (reportToSelect) {
            setSelectedReport(reportToSelect);
            const content = getReportContent(reportToSelect);
            setMdReport(content || '');
          }
        }
      } catch (error) {
        log.error('Error restoring selected report:', error);
      }
    };

    if (reports.length > 0 && !selectedReport) {
      restoreSelectedReport();
    }
  }, [reports, selectedReport]);

  // Keyboard shortcuts
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Cleanup insert mode and auto-save on unmount
  useEffect(() => {
    return () => {
      if (isInsertMode) {
        stopInsertMode();
      }
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [isInsertMode, stopInsertMode]);

  // Save before unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      handleSave();
      stopInsertMode();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handleSave, stopInsertMode]);

  // ===== PUSH NOTIFICATION HANDLER =====
  useEffect(() => {
    const handleReportCompletedNotification = async (event) => {

      
      // Show notification to user
      showNotification('Un nouveau rapport est disponible', 'success');
      
      // Refresh the reports list
      if (!isRefreshingRef.current) {
        await handleRefresh();
      }
    };

    // Listen for report completed events from push notifications
    window.addEventListener('reportCompleted', handleReportCompletedNotification);

    return () => {
      window.removeEventListener('reportCompleted', handleReportCompletedNotification);
    };
  }, [handleRefresh, showNotification]);

  // ===== RENDER =====
  const containerClass = isCapacitor ? 'mobile-report-list-container' : 'report-list-container';
  const platformClass = isElectron ? 'electron' : (isCapacitor ? 'capacitor' : 'native-web');
  const panelOpenClass = isWebOnly && isRecorderPanelOpen ? 'recorder-panel-open' : '';

  return (
    <div className={`${containerClass} ${platformClass} ${panelOpenClass}`} onKeyDown={handleKeyDown}>
      {/* Local Loading Animation */}
      <ReportListLoader isLoading={loading} text={loadingText} />
      
      {/* Notification */}
      {notification && (
        <Notification
          key={notification.id}
          id={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}



      {/* Report Type Overlay */}
      {showReportTypeOverlay && (
        <div className="report-type-overlay" onClick={() => setShowReportTypeOverlay(false)}>
          <div className="report-type-modal" onClick={e => e.stopPropagation()}>
            <h3>Changer le modèle</h3>
            <select
              value={selectedReport?.reportType || ''}
              onChange={e => handleChangeReportType(e.target.value)}
            >
              {(favoriteReportTypes || []).map((typeName) => (
                <option key={typeName} value={typeName}>
                  {formatReportTypeName(typeName)}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              collapseOnSmallScreen={false}
              onClick={() => setShowReportTypeOverlay(false)}
            >
              <FaTimes /> Fermer
            </Button>
          </div>
        </div>
      )}

    
                  
      {/* Annex Panel Overlay */}
      {showAnnexPanel && selectedReport && (
        <div className="report-type-overlay" onClick={() => setShowAnnexPanel(false)}>
          <div className="report-type-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>

              <h3 style={{ marginTop: 0 }}>Données extraites</h3>
              <textarea
                className="annex-textarea"
                readOnly
                value={selectedReport.annex || ''}
                style={{
                  width: '100%',
                  height: '300px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  background: '#fafafa'
                }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '12px' }}>
                <Button
                  icon={FaCopy}
                  variant="info"
                  onClick={() => {
                    copyTextToClipboard(selectedReport.annex || '').then(() => {
                      showNotification('Annexe copiée dans le presse-papiers', 'success');
                    }).catch(() => {
                      showNotification("Impossible de copier l'annexe", 'error');
                    });
                  }}
                >
                  Copier
                </Button>
                <Button
                  variant="secondary"
                  collapseOnSmallScreen={false}
                  onClick={() => setShowAnnexPanel(false)}
                >
                  <FaTimes /> Fermer
                </Button>
              </div>
          </div>
        </div>
      )}

      {/* Destinataire Panel Overlay */}
      {showDestinatairePanel && selectedReport && (
        <div className="report-type-overlay" onClick={() => setShowDestinatairePanel(false)}>
          <div className="report-type-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Destinataire</h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
              Voxia a trouvé cette adresse en ligne pour le destinataire de votre rapport. Vérifiez toujours la justesse de l'adresse avant tout envoi.
            </p>
            <textarea
              value={editedDestinataire}
              onChange={handleDestinataireChange}
              placeholder="Entrez l'adresse du destinataire..."
              style={{
                width: '100%',
                height: '150px',
                resize: 'vertical',
                boxSizing: 'border-box',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                background: '#fff',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '16px',
                lineHeight: '1.6'
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '12px' }}>
              <Button
                icon={FaCopy}
                variant="info"
                onClick={() => {
                  copyTextToClipboard(editedDestinataire).then(() => {
                    showNotification('Destinataire copié', 'info');
                  }).catch(() => {
                    showNotification("Impossible de copier le destinataire", 'error');
                  });
                }}
              >
                Copier
              </Button>
              <Button
                variant="secondary"
                collapseOnSmallScreen={false}
                onClick={() => setShowDestinatairePanel(false)}
              >
                <FaTimes /> Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Enterprise Selection Modal */}
      {showEnterpriseSelectionModal && (
        <div className="report-type-overlay" onClick={() => setShowEnterpriseSelectionModal(false)}>
          <div className="report-type-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h3>Créer un exemple organisation</h3>
            
            {/* Enterprise Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                Sélectionner une organisation:
              </label>
              <select
                value={selectedEnterpriseId || ''}
                onChange={(e) => setSelectedEnterpriseId(e.target.value)}
                style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">-- Choisir une organisation --</option>
                {availableEnterprises.map((enterprise) => (
                  <option key={enterprise.id} value={enterprise.id}>
                    {enterprise.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Report Type Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                Sélectionner un modèle:
              </label>
              {selectedEnterpriseId && (
                <select
                  value={selectedReportType || ''}
                  onChange={(e) => setSelectedReportType(e.target.value)}
                  style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">{enterpriseReportTypesLoading ? 'Chargement...' : '-- Choisir un modèle --'}</option>
                  {availableEnterpriseReportTypes.map((rt) => (
                    <option key={rt.id} value={rt.name}>
                      {formatReportTypeName(rt.name)}
                    </option>
                  ))}
                </select>
              )}
              {!selectedEnterpriseId && (
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                  Veuillez d'abord sélectionner une organisation
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
           
              <Button
                variant="secondary"
                collapseOnSmallScreen={false}
                onClick={() => {
                  setShowEnterpriseSelectionModal(false);
                  setSelectedEnterpriseId(null);
                  setSelectedReportType(null);
                }}
              >
                <FaTimes /> Annuler
              </Button>
              <Button
                variant="primary"
                onClick={() => createEnterpriseExample(selectedEnterpriseId, selectedReportType)}
                disabled={!selectedEnterpriseId || !selectedReportType || enterpriseExampleLoading}
                isSpinning={enterpriseExampleLoading}
              >
                <FaBuilding /> Créer l'exemple
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Context Menu */}
      {contextMenu.show && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 10000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="context-menu-item"
            onClick={() => {
              handleDeleteReport(contextMenu.reportId);
              handleCloseContextMenu();
            }}
          >
            <FaTimes /> Supprimer
          </button>
        </div>
      )}

      {/* Sidebar hover area for small screens */}
      {isSmallScreen && (!isSidebarVisible || !selectedReport) && (
        <div
          className={isCapacitor ? "mobile-sidebar-hover-area" : "sidebar-hover-area"}
          onMouseEnter={() => isSmallScreen && setIsSidebarVisible(true)}
        />
      )}

      {/* Sidebar */}
      <ReportListSidebar
        reports={reports}
        filteredReports={filteredReports}
        selectedReport={selectedReport}
        onReportClick={handleReportClick}
        onArchiveReport={handleArchiveReport}
        onDeleteReport={handleDeleteReport}
        onContextMenu={handleContextMenu}
        isRefreshing={isRefreshingRef.current}
        onRefresh={handleRefreshWithCooldown}
        loading={loading}
        refreshCooldown={refreshCooldown}
        searchQuery={searchQuery}
        onSearchChange={handleSearchWithDateReset}
        reportTypeFilter={reportTypeFilter}
        onReportTypeFilterChange={setReportTypeFilter}
        dateFilter={dateFilter}
        onDateFilterChange={handleDateFilterChange}
        showArchived={showArchived}
        onShowArchivedChange={setShowArchived}
        isEnterpriseAdmin={isEnterpriseAdmin}
        extractedReportTypes={extractedReportTypes}
        onExtractTypes={() => {}}
        showNotification={showNotification}
        isSidebarVisible={isSidebarVisible}
        onMouseLeave={() => isSmallScreen && setIsSidebarVisible(false)}
        isElectron={isElectron}
        currentVisibleGroup={currentVisibleGroup}
        statusCheckingReports={statusCheckingReports}
        statusCheckResults={statusCheckResults}
        isAdvancedSearchActive={isAdvancedSearchActive}
        onSubmitAdvancedSearch={handleSubmitAdvancedSearch}
        onStopAdvancedSearch={handleStopAdvancedSearch}
        onShowLiveDictations={handleShowLiveDictations}
      />

      {/* Main Content */}
      <ReportListMainContent
        selectedReport={selectedReport}
        mdReport={mdReport}
        onMdReportChange={handleEditorChange}
        isInsertMode={isInsertMode}
        sections={sections}
        onSendToServer={handleSendToServer}
        isSending={isSendingRef.current}
        onArchiveReport={handleArchiveReport}
        onDeleteReport={handleDeleteReport}
        onContextMenu={handleContextMenu}
        onCopy={handleCopy}
        onInsertion={handleInsertion}
        onSendEmail={handleSendEmail}
        onToggleExportMenu={handleToggleExportMenu}
        onToggleTextOptions={handleToggleTextOptions}
        showTextOptions={showTextOptions}
        showExportMenu={showExportMenu}
  
        onToggleReportTypeOverlay={handleToggleReportTypeOverlay}
        onToggleAnnexPanel={handleToggleAnnexPanel}
        onToggleDestinatairePanel={handleToggleDestinatairePanel}
        onExportButtonClick={handleExportButtonClick}
        onExportOpenClick={handleExportOpenClick}
        onExportDownloadClick={handleExportDownloadClick}
        onSetDefaultFolder={handleSetDefaultFolder}
        defaultExportDir={defaultExportDir}
        onToggleSortByDate={handleToggleSortByDate}
        sortMonthDay={sortMonthDay}
        onExportSectionsCSV={handleExportSectionsCSV}
        onUndo={handleUndo}
        isEnterpriseAdmin={isEnterpriseAdmin}
        onCreateEnterpriseExample={handleCreateEnterpriseExample}
        enterpriseExampleLoading={enterpriseExampleLoading}
        isSystemAdmin={profile?.isAdmin || false}
        onCreateAdminDefaultExample={handleCreateAdminDefaultExample}
        adminDefaultExampleLoading={adminExampleLoading}
        onBullseyeChange={handleBullseyeChange}
        onCopySection={handleCopySection}
        copiedSections={copiedSections}
        caretPos={caretPos}
        onUpdateCaretPosition={updateCaretPosition}
        isSidebarVisible={isSidebarVisible}
        isSmallScreen={isSmallScreen}
        onSetSidebarVisible={setIsSidebarVisible}
        onSidebarIndicatorClick={handleSidebarIndicatorClick}
        textareaRef={textareaRef}
        openFullScreenEditor={openFullScreenEditor}
        fullScreenEditorProps={fullScreenEditorProps}
        isFullScreenEnabled={isFullScreenEnabled}
        isSilverOrGoldOrEnterprise={isSilverOrGoldOrEnterprise}
        onGeneratePrescription={handleGeneratePrescription}
        isGeneratingPrescription={isGeneratingPrescription}
        onPrint={handlePrint}
        hasUncertaintyMarkers={hasUncertaintyMarkers}
        uncertaintyPreview={getUncertaintyPreview(mdReport)}
        showSchema={showSchema}
        setShowSchema={setShowSchema}
        onCleanUncertaintyMarkers={handleCleanUncertaintyMarkers}
      />
      
      {/* FullScreen Editor */}
      {isFullScreenEnabled && (
        <FullScreenTextEditor {...fullScreenEditorProps} />
      )}

      {/* Recorder Side Panel (web only) */}
      <RecorderSidePanel />
    </div>
  );
}